import { type MaybeRef, toValue } from 'vue';

import { TasksStacklets } from '@/shared/tasks';

import {
  attachFirewallToStacklet,
  createStacklet,
  createStackletSnapshot,
  deleteStacklet,
  detachFirewallFromStacklet,
  detachVolumeFromStacklet,
  disableStackletBackups,
  enableStackletBackups,
  enableStackletPrivateIP,
  powerCycleStacklet,
  powerOffStacklet,
  powerOnStacklet,
  renameStacklet,
  resetStackletPassword,
  resizeStacklet,
  restoreStackletSnapshot,
  saveStackletTags,
  toggleStackletRecoveryMode,
} from './api';
import type { CreateStackletData } from './types';

export const createStackletMutation = () => ({
  mutationFn: (data: CreateStackletData) => createStacklet(data),
});

export const enableStackletBackupsMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => enableStackletBackups(toValue(stackletId)),
});

export const disableStackletBackupsMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => disableStackletBackups(toValue(stackletId)),
});

export const attachFirewallToStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (firewallUuid: string) => attachFirewallToStacklet(toValue(stackletId), firewallUuid),
});

export const detachFirewallFromStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (firewallUuid: string) =>
    detachFirewallFromStacklet(toValue(stackletId), firewallUuid),
});

export const detachVolumeFromStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () =>
    detachVolumeFromStacklet(toValue(stackletId), {
      action: TasksStacklets.VOLUME_DETACH.name,
    }),
});

export const enableStackletPrivateIPMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => enableStackletPrivateIP(toValue(stackletId)),
});

export const toggleStackletRecoveryModeMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (data: { action: string }) => toggleStackletRecoveryMode(toValue(stackletId), data),
});

export const resizeStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (data: { disk: boolean; size: string }) => resizeStacklet(toValue(stackletId), data),
});

export const createStackletSnapshotMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (data: { name: string; action: string }) =>
    createStackletSnapshot(toValue(stackletId), data),
});

export const restoreStackletSnapshotMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (imageId: number) => restoreStackletSnapshot(toValue(stackletId), imageId),
});

export const powerOnStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => powerOnStacklet(toValue(stackletId)),
});

export const powerOffStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => powerOffStacklet(toValue(stackletId)),
});

export const powerCycleStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => powerCycleStacklet(toValue(stackletId)),
});

export const resetStackletPasswordMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => resetStackletPassword(toValue(stackletId)),
});

export const renameStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (name: string) => renameStacklet(toValue(stackletId), name),
});

export const saveStackletTagsMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: (tags: string[]) => saveStackletTags(toValue(stackletId), tags),
});

export const deleteStackletMutation = (stackletId: MaybeRef<number>) => ({
  mutationFn: () => deleteStacklet(toValue(stackletId)),
});

export const deleteStackletsMutation = () => ({
  mutationFn: (ids: number[]) => Promise.all(ids.map((id) => deleteStacklet(id))),
});
